import React, { useEffect, useState } from "react";
import { useActiveAccount, useActiveWallet } from "thirdweb/react";
import { ThirdwebWalletConnect } from "../components/ThirdwebWalletConnect";
import { ethers } from "ethers";
import { getEventsRecursive } from "../utils/ethEvents";
import {
  getRipndipImageUrl,
  getRipndipImageUrlFromVariant,
  getRipndipVariantName
} from "../utils/ripndip";

interface NFT {
  tokenId: string;
  name: string;
  image: string;
  variant?: string;
}

const ARTICLE_URL = "https://www.instagram.com/ripndip/";
const SHOP_URL = "https://www.instagram.com/ponkehq/";

const Ripndip = () => {
  const wallet = useActiveWallet();
  const account = useActiveAccount();
  const address = account?.address;
  const contractAddress = process.env.REACT_APP_RIPNDIP_CONTRACT_ADDRESS;
  const rpcUrl = process.env.REACT_APP_RIPNDIP_RPC_URL;

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [showAllNFTs, setShowAllNFTs] = useState(false);

  useEffect(() => {
    if (address) {
      loadUserNFTs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  const loadUserNFTs = async () => {
    console.log('[Ripndip] loadUserNFTs called', { address, contractAddress, rpcUrl });
    if (!address || !contractAddress || !rpcUrl) {
      console.warn('[Ripndip] Early exit — missing address/contractAddress/rpcUrl', { address, contractAddress, rpcUrl });
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch("/RipndipAbi.json");
      const abi = await response.json();
      console.log('[Ripndip] ABI loaded, entries:', abi.length);

      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      const currentBlock = await provider.getBlockNumber();
      console.log('[Ripndip] RPC connected, currentBlock:', currentBlock);

      const contract = new ethers.Contract(contractAddress, abi, provider);

      const eoaAccount =
        (wallet as any)?.getAdminAccount?.() ||
        (wallet as any)?.getAccount?.() ||
        account;
      const eoaAddress = eoaAccount?.address;
      const balanceAddress = eoaAddress || address;
      console.log('[Ripndip] Addresses', { address, eoaAddress, balanceAddress });

      const balance = await contract.balanceOf(balanceAddress);
      const nftCount = Number(balance);
      console.log('[Ripndip] balanceOf', balanceAddress, '=', nftCount);

      if (nftCount === 0) {
        console.log('[Ripndip] Balance is 0 — no NFTs for this address');
        setNfts([]);
        return;
      }

      const deployBlock = parseInt(
        process.env.REACT_APP_RIPNDIP_CONTRACT_DEPLOY_BLOCK || "0",
        10
      );
      console.log('[Ripndip] deployBlock:', deployBlock, '| currentBlock:', currentBlock);

      const addressLower = balanceAddress.toLowerCase();
      const resolveOwnedTokenIds = async (fromBlock: number) => {
        console.log('[Ripndip] Querying Transfer events fromBlock:', fromBlock, 'toBlock:', currentBlock);
        const [toEvents, fromEvents] = await Promise.all([
          getEventsRecursive({
            contract,
            eventName: "Transfer",
            args: [null, balanceAddress],
            fromBlock
          }),
          getEventsRecursive({
            contract,
            eventName: "Transfer",
            args: [balanceAddress, null],
            fromBlock
          })
        ]);
        console.log('[Ripndip] Transfer events — to:', toEvents.length, '| from:', fromEvents.length);

        const combined = [...toEvents, ...fromEvents];
        combined.sort((a, b) => {
          if (a.blockNumber !== b.blockNumber) {
            return b.blockNumber - a.blockNumber;
          }
          return (b.logIndex ?? 0) - (a.logIndex ?? 0);
        });

        const ownedTokenIds: string[] = [];
        const seenTokenIds = new Set<string>();

        for (const ev of combined) {
          const args: any = ev.args;
          if (!args) continue;
          const tokenIdStr = args.tokenId?.toString?.();
          if (!tokenIdStr || seenTokenIds.has(tokenIdStr)) continue;
          seenTokenIds.add(tokenIdStr);
          if (String(args.to).toLowerCase() === addressLower) {
            ownedTokenIds.push(tokenIdStr);
            if (ownedTokenIds.length >= nftCount) break;
          }
        }

        console.log('[Ripndip] Resolved ownedTokenIds:', ownedTokenIds);
        return ownedTokenIds;
      };

      let ownedTokenIds = await resolveOwnedTokenIds(deployBlock);

      if (ownedTokenIds.length < nftCount) {
        console.log('[Ripndip] Token count mismatch (found', ownedTokenIds.length, 'expected', nftCount, ') — retrying from block 0');
        ownedTokenIds = await resolveOwnedTokenIds(0);
      }

      console.log('[Ripndip] Final ownedTokenIds:', ownedTokenIds);

      const userNFTs = await Promise.all(
        ownedTokenIds.map(async (tokenIdStr) => {
          try {
            const tokenUri = await contract.tokenURI(tokenIdStr);
            console.log('[Ripndip] tokenURI for', tokenIdStr, ':', tokenUri);
            const metadata = await fetch(tokenUri).then((res) => res.json());
            const variant = metadata?.attributes?.find?.(
              (attribute: any) => attribute?.trait_type === "Variant"
            )?.value;
            console.log('[Ripndip] metadata for tokenId', tokenIdStr, '— variant:', variant);

            return {
              tokenId: tokenIdStr,
              name:
                metadata?.name ||
                `RipndipxPonke CoA #${tokenIdStr.padStart(3, "0")}`,
              image: getRipndipImageUrlFromVariant(variant),
              variant: variant || getRipndipVariantName()
            };
          } catch (metaErr) {
            console.warn('[Ripndip] metadata fetch failed for tokenId', tokenIdStr, metaErr);
            return {
              tokenId: tokenIdStr,
              name: `RipndipxPonke CoA #${tokenIdStr.padStart(3, "0")}`,
              image: getRipndipImageUrl(),
              variant: getRipndipVariantName()
            };
          }
        })
      );

      console.log('[Ripndip] Final NFTs to display:', userNFTs);
      setNfts(userNFTs);
    } catch (err) {
      console.error('[Ripndip] loadUserNFTs error:', err);
      setError("Failed to load Digital Collectibles");
    } finally {
      setIsLoading(false);
    }
  };

  if (!address && !isLoading) {
    return (
      <div className="flex justify-center items-center bg-gray-50 min-h-[calc(100dvh-149.5px)] p-6">
        <div className="mx-auto max-w-md text-center">
          <div className="p-8 bg-white rounded-lg shadow-md">
            <img
              src="/images/223-logo.png"
              alt="223 Logo"
              className="mx-auto mb-4 max-w-[100px]"
            />
            <h1 className="mb-4 text-2xl font-bold text-gray-900">
              Connect Your Wallet
            </h1>
            <p className="mb-8 text-gray-600">
              Connect your wallet to view your 223 Digital Collectibles and
              reveal additional rewards.
            </p>
            {error && (
              <div className="px-4 py-3 mb-4 text-red-700 bg-red-100 rounded border border-red-400">
                {error}
              </div>
            )}
            <div className="flex justify-center">
              <ThirdwebWalletConnect showDropdown={false} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5">
      <div className="border border-black rounded-[14px] p-3.5 w-full">
        <div className="flex justify-between items-center">
          <h6 className="font-bold leading-none">My 223 Portal</h6>

          <div className="flex items-center gap-1.5">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.3333 9.33333C11.5101 9.33333 11.6797 9.2631 11.8047 9.13807C11.9298 9.01305 12 8.84348 12 8.66667C12 8.48986 11.9298 8.32029 11.8047 8.19526C11.6797 8.07024 11.5101 8 11.3333 8C11.1565 8 10.987 8.07024 10.8619 8.19526C10.7369 8.32029 10.6667 8.48986 10.6667 8.66667C10.6667 8.84348 10.7369 9.01305 10.8619 9.13807C10.987 9.2631 11.1565 9.33333 11.3333 9.33333ZM11.3333 12C11.5101 12 11.6797 11.9298 11.8047 11.8047C11.9298 11.6797 12 11.5101 12 11.3333C12 11.1565 11.9298 10.987 11.8047 10.8619C11.6797 10.7369 11.5101 10.6667 11.3333 10.6667C11.1565 10.6667 10.987 10.7369 10.8619 10.8619C10.7369 10.987 10.6667 11.1565 10.6667 11.3333C10.6667 11.5101 10.7369 11.6797 10.8619 11.8047C10.987 11.9298 11.1565 12 11.3333 12ZM8.66667 8.66667C8.66667 8.84348 8.59643 9.01305 8.4714 9.13807C8.34638 9.2631 8.17681 9.33333 8 9.33333C7.82319 9.33333 7.65362 9.2631 7.5286 9.13807C7.40357 9.01305 7.33333 8.84348 7.33333 8.66667C7.33333 8.48986 7.40357 8.32029 7.5286 8.19526C7.65362 8.07024 7.82319 8 8 8C8.17681 8 8.34638 8.07024 8.4714 8.19526C8.59643 8.32029 8.66667 8.48986 8.66667 8.66667ZM8.66667 11.3333C8.66667 11.5101 8.59643 11.6797 8.4714 11.8047C8.34638 11.9298 8.17681 12 8 12C7.82319 12 7.65362 11.9298 7.5286 11.8047C7.40357 11.6797 7.33333 11.5101 7.33333 11.3333C7.33333 11.1565 7.40357 10.987 7.5286 10.8619C7.65362 10.7369 7.82319 10.6667 8 10.6667C8.17681 10.6667 8.34638 10.7369 8.4714 10.8619C8.59643 10.987 8.66667 11.1565 8.66667 11.3333ZM4.66667 9.33333C4.84348 9.33333 5.01305 9.2631 5.13807 9.13807C5.2631 9.01305 5.33333 8.84348 5.33333 8.66667C5.33333 8.48986 5.2631 8.32029 5.13807 8.19526C5.01305 8.07024 4.84348 8 4.66667 8C4.48986 8 4.32029 8.07024 4.19526 8.19526C4.07024 8.32029 4 8.48986 4 8.66667C4 8.84348 4.07024 9.01305 4.19526 9.13807C4.32029 9.2631 4.48986 9.33333 4.66667 9.33333ZM4.66667 12C4.84348 12 5.01305 11.9298 5.13807 11.8047C5.2631 11.6797 5.33333 11.5101 5.33333 11.3333C5.33333 11.1565 5.2631 10.987 5.13807 10.8619C5.01305 10.7369 4.84348 10.6667 4.66667 10.6667C4.48986 10.6667 4.32029 10.7369 4.19526 10.8619C4.07024 10.987 4 11.1565 4 11.3333C4 11.5101 4.07024 11.6797 4.19526 11.8047C4.32029 11.9298 4.48986 12 4.66667 12Z"
                fill="black"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M4.66665 1.16667C4.79926 1.16667 4.92644 1.21935 5.0202 1.31312C5.11397 1.40689 5.16665 1.53406 5.16665 1.66667V2.17534C5.60798 2.16667 6.09398 2.16667 6.62865 2.16667H9.37065C9.90598 2.16667 10.392 2.16667 10.8333 2.17534V1.66667C10.8333 1.53406 10.886 1.40689 10.9798 1.31312C11.0735 1.21935 11.2007 1.16667 11.3333 1.16667C11.4659 1.16667 11.5931 1.21935 11.6869 1.31312C11.7806 1.40689 11.8333 1.53406 11.8333 1.66667V2.21801C12.0067 2.23134 12.1709 2.24823 12.326 2.26867C13.1073 2.37401 13.74 2.59534 14.2393 3.094C14.738 3.59334 14.9593 4.22601 15.0647 5.00734C15.1667 5.76734 15.1667 6.73734 15.1667 7.96267V9.37067C15.1667 10.596 15.1667 11.5667 15.0647 12.326C14.9593 13.1073 14.738 13.74 14.2393 14.2393C13.74 14.738 13.1073 14.9593 12.326 15.0647C11.566 15.1667 10.596 15.1667 9.37065 15.1667H6.62998C5.40465 15.1667 4.43398 15.1667 3.67465 15.0647C2.89332 14.9593 2.26065 14.738 1.76132 14.2393C1.26265 13.74 1.04132 13.1073 0.935984 12.326C0.833984 11.566 0.833984 10.596 0.833984 9.37067V7.96267C0.833984 6.73734 0.833984 5.76667 0.935984 5.00734C1.04132 4.22601 1.26265 3.59334 1.76132 3.094C2.26065 2.59534 2.89332 2.37401 3.67465 2.26867C3.83021 2.24823 3.99443 2.23134 4.16732 2.21801V1.66667C4.16732 1.53418 4.2199 1.4071 4.31353 1.31335C4.40715 1.2196 4.53416 1.16685 4.66665 1.16667ZM3.80665 3.26C3.13665 3.35001 2.74998 3.51934 2.46798 3.80134C2.18598 4.08334 2.01665 4.47001 1.92665 5.14001C1.91154 5.25334 1.89865 5.37312 1.88798 5.49934H14.112C14.1013 5.37312 14.0884 5.25312 14.0733 5.13934C13.9833 4.46934 13.814 4.08267 13.532 3.80067C13.25 3.51867 12.8633 3.34934 12.1927 3.25934C11.508 3.16734 10.6047 3.16601 9.33332 3.16601H6.66665C5.39532 3.16601 4.49265 3.168 3.80665 3.26ZM1.83332 8.00001C1.83332 7.43067 1.83332 6.93534 1.84198 6.50001H14.158C14.1667 6.93534 14.1667 7.43067 14.1667 8.00001V9.33334C14.1667 10.6047 14.1653 11.508 14.0733 12.1933C13.9833 12.8633 13.814 13.25 13.532 13.532C13.25 13.814 12.8633 13.9833 12.1927 14.0733C11.508 14.1653 10.6047 14.1667 9.33332 14.1667H6.66665C5.39532 14.1667 4.49265 14.1653 3.80665 14.0733C3.13665 13.9833 2.74998 13.814 2.46798 13.532C2.18598 13.25 2.01665 12.8633 1.92665 12.1927C1.83465 11.508 1.83332 10.6047 1.83332 9.33334V8.00001Z"
                fill="black"
              />
            </svg>
            <span className="text-xs">
              {new Date().toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric"
              })}
            </span>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="inline-block w-8 h-8 rounded-full border-b-2 border-gray-900 animate-spin"></div>
            <p className="ml-2 text-gray-600">
              Loading your Digital Collectibles...
            </p>
          </div>
        ) : nfts.length === 0 ? (
          <div className="py-8 text-center">
            <p className="text-gray-600">
              You don't own any 223 Digital Collectibles yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3 mt-4">
            <div className="grid grid-cols-2 gap-3">
              {(showAllNFTs ? nfts : nfts.slice(0, 4)).map((nft) => (
                <div
                  key={nft.tokenId}
                  className="relative rounded-[14px] overflow-hidden aspect-square bg-[#F7F0D4]"
                >
                  <img
                    src={nft.image}
                    alt={nft.name}
                    className="object-cover w-full h-full"
                  />
                  <div className="absolute inset-x-2.5 bottom-2.5 px-2 py-1 border border-white backdrop-blur-md rounded-lg">
                    <h3 className="text-lg leading-normal font-bold text-white">
                      {nft.variant}
                    </h3>

                    <p className="text-[10px] text-white">Claimed</p>
                  </div>
                </div>
              ))}
            </div>

            {nfts.length > 4 && (
              <div className="flex justify-center">
                <button
                  onClick={() => setShowAllNFTs(!showAllNFTs)}
                  className="px-6 py-2 text-sm font-semibold text-black bg-white border-2 border-black rounded-[50px] hover:bg-black hover:text-white transition-colors"
                >
                  {showAllNFTs ? "Show Less" : `Show All (${nfts.length})`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-col space-y-3">
        <PromoCard
          href={ARTICLE_URL}
          image="/images/ripndip/instagram-1.png"
          imageAlt="Follow Ripndip"
          buttonLabel="FOLLOW RIPNDIP"
          buttonClassName="text-white"
        />
        <PromoCard
          href={SHOP_URL}
          image="/images/ripndip/instagram-2.png"
          imageAlt="Follow Ponke"
          buttonLabel="FOLLOW PONKE"
          buttonClassName="text-white"
        />
      </div>
    </div>
  );
};

const PromoCard = ({
  href,
  image,
  imageAlt,
  buttonLabel,
  buttonClassName,
  buttonPosition = "top"
}: {
  href: string;
  image: string;
  imageAlt: string;
  buttonLabel: string;
  buttonClassName?: string;
  buttonPosition?: "top" | "bottom";
}) => {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="block overflow-hidden rounded-[14px] relative"
    >
      <img
        src={image}
        alt={imageAlt}
        className="rounded-[14px] w-full"
      />
      <div
        className={`absolute inset-x-3 ${
          buttonPosition === "bottom" ? "bottom-3" : "top-3"
        }`}
      >
        <div
          className={`h-10 rounded-[10px] border border-white/30 bg-white/10 backdrop-blur-md flex items-center justify-center px-4 text-sm font-bold tracking-[0.02em] shadow-[0_8px_24px_rgba(255,255,255,0.08)] ${buttonClassName || "text-white"}`}
        >
          {buttonLabel}
        </div>
      </div>
    </a>
  );
};

export default Ripndip;
